/**
 * The surface every panel sits on. Prototype: docs/HRMS.jsx line 555-557
 * (surface background, 1 px line border, 16 px radius — nothing else).
 *
 * A clickable card stays a `div` with `role="button"` rather than becoming a real
 * `<button>`, because the prototype nests its own interactive elements inside cards
 * (the docs chip in the Teammates grid) and a button inside a button is invalid HTML.
 * Keyboard parity is restored with tabIndex + Enter/Space handling.
 */
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from "react";
import "./ui.css";

export interface CardProps {
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  onClick?: (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void;
  /** Accessible name for a clickable card, e.g. "Open Rohan Mehta". */
  label?: string;
  title?: string;
  id?: string;
}

export function Card({ children, style, className, onClick, label, title, id }: CardProps) {
  const interactive = Boolean(onClick);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    // Space would scroll the page; the card's activation replaces that.
    event.preventDefault();
    onClick(event);
  };

  return (
    <div
      id={id}
      className={["ui-card", interactive ? "ui-card--interactive" : null, className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      title={title}
      onClick={onClick}
      onKeyDown={interactive ? handleKeyDown : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? label : undefined}
    >
      {children}
    </div>
  );
}

export default Card;
